/**
 * Map colors, read straight from the design-system CSS variables.
 *
 * MapLibre paint properties can't reference CSS custom properties, so instead
 * of duplicating hex values in JS (which would drift from the tokens) we read
 * the computed values off the document at runtime. Re-reading after a theme
 * change yields the dark-theme values — the tokens stay the single source of
 * truth, per the brief.
 */
export interface MapColors {
  plumeLow: string;
  plumeModerate: string;
  plumeHigh: string;
  plumeSevere: string;
  plumeUnknown: string;
  plumeLowStroke: string;
  plumeModerateStroke: string;
  plumeHighStroke: string;
  plumeSevereStroke: string;
  plumeUnknownStroke: string;
  borderCountry: string;
  borderRegion: string;
  aoiPoint: string;
  aoiPointHalo: string;
  aoiZoneFill: string;
  aoiZoneStroke: string;
  selectionGlow: string;
  textPrimary: string;
  surfaceBase: string;
}

export function readMapColors(): MapColors {
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string) => cs.getPropertyValue(name).trim();
  return {
    plumeLow: v("--plume-low"),
    plumeModerate: v("--plume-moderate"),
    plumeHigh: v("--plume-high"),
    plumeSevere: v("--plume-severe"),
    plumeUnknown: v("--plume-unknown"),
    plumeLowStroke: v("--plume-low-stroke"),
    plumeModerateStroke: v("--plume-moderate-stroke"),
    plumeHighStroke: v("--plume-high-stroke"),
    plumeSevereStroke: v("--plume-severe-stroke"),
    plumeUnknownStroke: v("--plume-unknown-stroke"),
    borderCountry: v("--border-country"),
    borderRegion: v("--border-region"),
    aoiPoint: v("--aoi-point"),
    aoiPointHalo: v("--aoi-point-halo"),
    aoiZoneFill: v("--aoi-zone-fill"),
    aoiZoneStroke: v("--aoi-zone-stroke"),
    selectionGlow: v("--selection-glow"),
    textPrimary: v("--text-primary"),
    surfaceBase: v("--surface-base"),
  };
}

/**
 * A MapLibre `match` expression from a grade property to its token color.
 * Defaults to the plume's own `grade`; sources pass `maxGrade` (their worst
 * detection) so a source point is colored by its emission rate too.
 */
export function gradeColorExpression(colors: MapColors, prop = "grade"): unknown {
  return [
    "match",
    ["get", prop],
    "low", colors.plumeLow,
    "moderate", colors.plumeModerate,
    "high", colors.plumeHigh,
    "severe", colors.plumeSevere,
    colors.plumeUnknown,
  ];
}

/**
 * The same mapping for outlines, in the darkened variant of each grade.
 *
 * An outline in the fill's own colour disappears into it and stops holding the
 * shape of a small polygon; a black one holds the shape but reads as dirt over
 * this muted palette. The darkened grade does both jobs.
 */
export function gradeStrokeExpression(colors: MapColors, prop = "grade"): unknown {
  return [
    "match",
    ["get", prop],
    "low", colors.plumeLowStroke,
    "moderate", colors.plumeModerateStroke,
    "high", colors.plumeHighStroke,
    "severe", colors.plumeSevereStroke,
    colors.plumeUnknownStroke,
  ];
}
